/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsRevoke } from "../funcs/identityProviderConnectionsRevoke.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RevokeIdentityProviderConnectionRequest,
  RevokeIdentityProviderConnectionSecurity,
} from "../models/operations/revokeidentityproviderconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RevokeIdentityProviderConnectionMutationVariables = {
  request: RevokeIdentityProviderConnectionRequest;
  security?: RevokeIdentityProviderConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type RevokeIdentityProviderConnectionMutationData =
  OktaIdentityProviderConnection;

export type RevokeIdentityProviderConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * revoke identityProviderConnections
 *
 * @remarks
 * Revoke the connection: withdraw every signing key from the JWKS, disable the key material, and tombstone the connection so a new one can be created. Idempotent. Requires org:admin.
 */
export function useRevokeIdentityProviderConnectionMutation(
  options?: MutationHookOptions<
    RevokeIdentityProviderConnectionMutationData,
    RevokeIdentityProviderConnectionMutationError,
    RevokeIdentityProviderConnectionMutationVariables
  >,
): UseMutationResult<
  RevokeIdentityProviderConnectionMutationData,
  RevokeIdentityProviderConnectionMutationError,
  RevokeIdentityProviderConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRevokeIdentityProviderConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRevokeIdentityProviderConnection(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "revoke"];
}

export function buildRevokeIdentityProviderConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RevokeIdentityProviderConnectionMutationVariables,
  ) => Promise<RevokeIdentityProviderConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyRevokeIdentityProviderConnection(),
    mutationFn: function revokeIdentityProviderConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<RevokeIdentityProviderConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsRevoke(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
